import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AllDataBreeding } from "../../../../api/breeding";
import { enumsectionToI18nKey } from "../../../../generated/enums";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { useAdvancedSearch } from "../../../../hooks/useAdvancedSearch";
import {
  compareText,
  DataTable,
  DataTableSkeletonRows,
  DataTableSort,
  nextSort,
} from "../../../common/DataTable";
import { EmptyState } from "../../../common/EmptyState";
import { EntityCardSkeletons } from "../../../common/EntityCard";
import { MobileSort, sortOptions } from "../../../common/MobileSort";
import {
  clampPage,
  Pagination,
  TABLE_PAGE_SIZE,
} from "../../../common/Pagination";
import { TableFilters } from "../../../common/TableFilters";
import { Header, HeaderAction } from "../../../Header";
import { sectionToIcon } from "../../../type/default";
import { AssociateDeviceModal } from "../../device/AssociateDeviceModal";
import { installerBreedingPath } from "../farmer/breedingRoutes";
import { CreateClientBreedingModal } from "../farmer/CreateClientBreedingModal";
import {
  breedingNamesOf,
  UnassignedDevicesCard,
} from "../farmer/UnassignedDevicesCard";
import { useFarmerBreedings } from "../farmer/useFarmerBreedings";
import {
  CLIENT_BREEDING_SORT_VALUES,
  ClientBreedingColumnKey,
  ClientBreedingMobileCard,
  ClientBreedingRow,
  useClientBreedingColumns,
} from "./ClientBreedingRow";

const FAVORITES_KEY = "breedingFavorites";

const readFavorites = (): string[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const searchableFields = (entry: AllDataBreeding) => [
  entry.breeding.name,
  entry.breeding.city,
  entry.breeding.zip_code,
  entry.company.company_name,
  ...entry.buildings.map((building) => building.name),
  ...entry.devices.flatMap((device) => [device.name, device.uuid]),
  ...entry.accounts.flatMap((account) => [
    account.first_name,
    account.last_name,
    account.email,
  ]),
];

/** Élevages des clients d'un installateur : une ligne par élevage partagé. */
export const ClientBreedingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breedings, unassignedDevices, isLoading, hasError, reload } =
    useFarmerBreedings();
  const { getAccountStorage } = useAccountStorage();
  // Le collaborateur consulte et associe des produits, il ne crée pas d'élevage.
  const canCreateBreeding = getAccountStorage()?.type === "installer";
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<DataTableSort<ClientBreedingColumnKey>>();
  const [favorites, setFavorites] = useState<string[]>(readFavorites);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAssociateOpen, setIsAssociateOpen] = useState(false);
  const columns = useClientBreedingColumns();

  const filtered = useAdvancedSearch({
    searchTerm,
    data: breedings,
    fields: searchableFields,
  });

  // Les favoris restent en tête ; le tri de colonne s'applique à l'intérieur de chaque groupe.
  const sorted = useMemo(() => {
    const favoritesSet = new Set(favorites);
    const isFav = (entry: AllDataBreeding) =>
      favoritesSet.has(String(entry.breeding.id));
    const sortValue = sort ? CLIENT_BREEDING_SORT_VALUES[sort.key] : undefined;
    return [...filtered].sort((a, b) => {
      if (isFav(a) !== isFav(b)) return isFav(a) ? -1 : 1;
      if (!sort || !sortValue) return 0;
      const order = compareText(sortValue(a), sortValue(b));
      return sort.direction === "asc" ? order : -order;
    });
  }, [filtered, favorites, sort]);

  const currentPage = clampPage(page, TABLE_PAGE_SIZE, sorted.length);
  const paged = sorted.slice(
    currentPage * TABLE_PAGE_SIZE,
    (currentPage + 1) * TABLE_PAGE_SIZE
  );
  const scrollKey = [
    currentPage,
    searchTerm,
    sort?.key ?? "",
    sort?.direction ?? "",
  ].join("|");

  const changeSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };
  const applySort = (next?: DataTableSort<ClientBreedingColumnKey>) => {
    setSort(next);
    setPage(0);
  };

  const toggleFavorite = (id: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const key = String(id);
    setFavorites((prev) => {
      const next = prev.includes(key)
        ? prev.filter((fav) => fav !== key)
        : [...prev, key];
      try {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      } catch {
        // Favoris non mémorisés (stockage indisponible) : la session garde l'état.
      }
      return next;
    });
  };

  const rowProps = (entry: AllDataBreeding) => ({
    entry,
    isFavorite: favorites.includes(String(entry.breeding.id)),
    onToggleFavorite: toggleFavorite(entry.breeding.id),
    onOpen: () => navigate(installerBreedingPath(entry.breeding.id)),
  });

  return (
    <div className="p-4">
      <Header
        title={t(enumsectionToI18nKey["section_breedings"])}
        icon={sectionToIcon.section_breedings}
        actions={
          <>
            {canCreateBreeding && (
              <HeaderAction
                icon="plus"
                label={t("breeding-create")}
                onClick={() => setIsCreateOpen(true)}
              />
            )}
            <HeaderAction
              icon="link"
              variant="secondary"
              label={t("device-link-title")}
              onClick={() => setIsAssociateOpen(true)}
            />
          </>
        }
      />

      {hasError ? (
        <p className="mt-6 text-red-500">{t("breedings-load-error")}</p>
      ) : (
        <div className="mt-3 rounded-lg">
          <TableFilters
            filterValue=""
            onFilterChange={() => undefined}
            filterOptions={[]}
            searchValue={searchTerm}
            onSearchChange={changeSearch}
          />
          {!isLoading && sorted.length === 0 ? (
            <EmptyState
              icon={sectionToIcon.section_breedings}
              title={
                breedings.length === 0
                  ? t("breeding-client-empty")
                  : t("table-no-result")
              }
            />
          ) : (
            <>
              <div className="md:hidden space-y-4">
                <MobileSort
                  options={sortOptions(columns)}
                  sort={sort}
                  onChange={applySort}
                />
                {isLoading && <EntityCardSkeletons />}
                {paged.map((entry) => (
                  <ClientBreedingMobileCard
                    key={entry.breeding.id}
                    {...rowProps(entry)}
                  />
                ))}
              </div>

              <div className="hidden md:block">
                <DataTable
                  columns={columns}
                  scrollKey={scrollKey}
                  sort={sort}
                  onSort={(key) => {
                    setSort((current) => nextSort(current, key));
                    setPage(0);
                  }}
                >
                  {isLoading ? (
                    <DataTableSkeletonRows columns={columns} />
                  ) : (
                    paged.map((entry) => (
                      <ClientBreedingRow
                        key={entry.breeding.id}
                        {...rowProps(entry)}
                      />
                    ))
                  )}
                </DataTable>
              </div>
              {!isLoading && (
                <Pagination
                  page={currentPage}
                  pageSize={TABLE_PAGE_SIZE}
                  total={sorted.length}
                  onPageChange={setPage}
                  showTotal={false}
                />
              )}
            </>
          )}
        </div>
      )}

      {!isLoading && !hasError && (
        <div className="mt-6">
          <UnassignedDevicesCard
            groups={unassignedDevices}
            breedingNames={breedingNamesOf(breedings)}
            onAttached={reload}
            showCompany
          />
        </div>
      )}

      {canCreateBreeding && (
        <CreateClientBreedingModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={(breedingId) =>
            navigate(installerBreedingPath(breedingId))
          }
        />
      )}
      <AssociateDeviceModal
        isOpen={isAssociateOpen}
        onClose={() => setIsAssociateOpen(false)}
        onAssociated={reload}
      />
    </div>
  );
};
